import dayjs from "dayjs";
import { useCheckoutPaymentStatus } from "../../../../hooks/api/v2/checkout/useCheckoutPaymentStatus";
import { useOrder } from "../../../../hooks/api/v2/order/useOrder";
import { getOrderStage } from "../../../../components";

const POLLING_WINDOW_MS = 5000; // How often do we want to poll
const WAIT_FOR_WINDOW = 10; // Up to how many minutes after payment creation do we want to poll for payment statuses

export const useCheckoutSuccessPageData = ({
  orderId,
}: {
  orderId: number;
}) => {
  const order = useOrder(orderId, { enabled: !!orderId });

  const checkoutPaymentStatusQuery = useCheckoutPaymentStatus(
    { orderId },
    {
      enabled: !!orderId,
      refetchInterval: (response) => {
        if (
          response.state.data?.status !== "pending" ||
          !response.state.data.paymentCreatedAt
        ) {
          return false;
        }

        const timeSinceCreated = dayjs().diff(
          dayjs(response.state.data.paymentCreatedAt),
          "minutes",
        );
        // Poll every POLLING_WINDOW_MS milliseconds as long as we're within the WAIT_FOR_WINDOW window since
        // the payment was created
        return timeSinceCreated < WAIT_FOR_WINDOW ? POLLING_WINDOW_MS : false;
      },
      refetchOnWindowFocus: false,
    },
  );

  const supplierName = order.data?.Supplier.Business.name ?? "";
  const supplierLogoUrl = order.data?.Supplier.Business.logoUrl ?? "";
  const supplierId = order.data?.SupplierId;
  const orderTotal = order.data?.price ?? 0;
  const orderStatus = getOrderStage(order.data);
  const paymentType = order.data?.paymentType ?? undefined;
  const orderItems =
    order.data?.OrderItems.map((orderItem) => ({
      id: orderItem.id,
      productId: orderItem.ProductId,
    })) ?? [];
  const orderVatPercentage = order.data?.vatPercentage ?? 0;
  const paymentStatus = checkoutPaymentStatusQuery.data?.status ?? "pending";
  const stripeAccountId = order.data?.Supplier.stripeAccountId ?? "";
  const paymentIntentId = order.data?.Payment?.paymentIntentId ?? "";
  const retailerBusinessId = order.data?.Retailer.Business.id;
  const twoOrder = order.data?.TwoOrder ?? null;
  const fulfillmentType = order.data?.fulfillmentType;
  const deliveryPlannedAt = order.data?.deliveryPlannedAt;
  const hasPaymentLink = Boolean(order.data?.PaymentLink);

  return {
    loading: order.isPending,
    supplierName,
    supplierLogoUrl,
    orderTotal,
    orderStatus,
    paymentType,
    orderItems,
    orderVatPercentage,
    paymentStatus,
    stripeAccountId,
    paymentIntentId,
    orderId,
    retailerBusinessId,
    supplierId,
    twoOrder,
    fulfillmentType,
    deliveryPlannedAt,
    hasPaymentLink,
  };
};
